import React, { useState, useEffect, useCallback } from 'react';
import positiveDot from '../../../assets/svgs/PositiveDot.svg';
import negativeDot from '../../../assets/svgs/NegativeDot.svg';
import { LoadingOrderOpsStatus } from 'constants/orderConstants';
import { Box } from '@mui/material';

const TIME_TO_WARN = 5 * 60 * 1000; // 5 minutes in milliseconds
const UPDATE_INTERVAL = 1000; // 1 second

interface Store {
  logo?: string;
  id?: string | number;
  fdStoreId?: string | number;
}

type PadFooterProps = {
  timeSinceOrderModified?: string;
  nextOrder?: string;
  loadingStatus?: LoadingOrderOpsStatus;
  store?: Store;
  order1ShortId?: string;
};

const formatTimeAgo = (elapsedMilliseconds: number): string => {
  if (elapsedMilliseconds < 0) return '0:00';
  
  // Convert milliseconds to seconds and minutes
  const totalSeconds = Math.floor(elapsedMilliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const PadFooter = ({
  timeSinceOrderModified,
  nextOrder,
  loadingStatus,
  store,
  order1ShortId
}: PadFooterProps) => {

  const getElapsedTimeInMilliseconds = (): number => {
    if (!timeSinceOrderModified) return 0;
    return Date.now() - new Date(timeSinceOrderModified).getTime();
  };

  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const updateElapsedTime = useCallback(() => {
    try {
      setElapsedTime(getElapsedTimeInMilliseconds());
    } catch (err) {
      setError('Error updating time');
      console.error('Time update error:', err);
    }
  }, [timeSinceOrderModified]);

  useEffect(() => {
    const timer = setInterval(updateElapsedTime, UPDATE_INTERVAL);
    return () => clearInterval(timer);
  }, [updateElapsedTime]);

  useEffect(() => {
    setElapsedTime(getElapsedTimeInMilliseconds());
    setError(null);
  }, [timeSinceOrderModified]);

  if (error) {
    return (
      <div className="h-32 px-4 p-2 border border-mannamono-line rounded-xl flex justify-center items-center">
        <span className="text-mannadanger-default">Error displaying time</span>
      </div>
    );
  }

  if (!order1ShortId || loadingStatus === LoadingOrderOpsStatus.DISPATCHED || 
      loadingStatus === LoadingOrderOpsStatus.DELIVERED) {
    return (
      <div className="h-32 px-4 p-2 border border-mannamono-line rounded-xl flex justify-between items-center text-mannamono-off-black-label">
        <span className="text-2xl">Next:</span>
        <div className="flex items-center gap-3">
          <span className="text-7xl">{nextOrder || ''}</span>
          {store?.logo && nextOrder && (
            <Box sx={{ 
              width: 48, 
              height: 48, 
              border: '1px solid #D9DBE9', 
              borderRadius: 1, 
              overflow: 'hidden',
              position: 'relative'
            }}>
              <img
                alt="store logo"
                src={store.logo}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%'
                }}
              />
            </Box>
          )}
        </div>
      </div>
    );
  }

  const isWarning = elapsedTime > TIME_TO_WARN;
  const dotToUse = isWarning ? negativeDot : positiveDot;

  return (
    <div className={`h-32 px-4 p-2 border border-mannamono-line rounded-xl flex justify-between items-center ${
      isWarning ? 'bg-mannadanger-default' : 'bg-white'
    }`}>
      <span className={`base-text ${
        isWarning ? 'text-white' : 'text-mannamono-off-black'
      }`}>Last updated:</span>
      <div className='flex gap-2 items-center'>
        <span className={`text-4xl ${
          isWarning ? 'text-white' : 'text-mannamono-off-black'
        }`}>
          {formatTimeAgo(elapsedTime)} ago
        </span>
        <img alt='Time status indicator' src={dotToUse} />
      </div>
    </div>
  );
};

export default PadFooter;